import { t } from "@airange/shared/i18n-identity-picker";
import { getIdentityStatusDisplay } from "@airange/shared/identity-status";
import type { ProColumns } from "@ant-design/pro-components";
import { Space, Tag } from "antd";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useCallback, useMemo } from "react";
import { formatAdminDateTime } from "./admin-list-columns";
import { useControlledTextHeaderFilter } from "./admin-list-filters";
import { IdentityUserText } from "./identity-user-text";
import { formatCustomFieldCellValue } from "./sso-organization-user-picker-model";
import type {
  SsoOrganizationPickerCustomFieldDefinition,
  SsoOrganizationPickerFilterValues,
  SsoOrganizationPickerUser,
} from "./sso-organization-user-picker-types";
import { WrappableTagList } from "./wrappable-tag-list";

export type SsoOrganizationUserPickerColumnsState = Record<string, { show: boolean }>;

export type UseSsoOrganizationUserPickerColumnsParams = {
  customFieldDefinitions: SsoOrganizationPickerCustomFieldDefinition[];
  filterValues: SsoOrganizationPickerFilterValues;
  setFilterValues: Dispatch<SetStateAction<SsoOrganizationPickerFilterValues>>;
  defaultColumnsState?: SsoOrganizationUserPickerColumnsState;
};

function formatMembershipSummary(record: SsoOrganizationPickerUser): string {
  const summary = record.organization_memberships_summary;
  return summary
    .map((item) => `${item.organization_name} / ${item.organization_unit_name}`)
    .join("; ");
}

function formatPlatformRoleAssignments(record: SsoOrganizationPickerUser): ReactNode {
  const assignments = record.platform_role_assignments;
  if (!assignments?.length) {
    return "-";
  }
  return (
    <Space size={[4, 4]} wrap>
      {assignments.map((assignment) => {
        const scope = assignment.organization_name || assignment.tenant_name;
        const label = scope ? `${assignment.platform_role} @ ${scope}` : assignment.platform_role;
        return <Tag key={assignment.id}>{label}</Tag>;
      })}
    </Space>
  );
}

export function getActiveCustomFieldDefinitions(
  customFieldDefinitions: SsoOrganizationPickerCustomFieldDefinition[],
) {
  return customFieldDefinitions
    .filter((item) => item.status === "active" && item.visibility !== "hidden")
    .sort((left, right) => left.sort_order - right.sort_order);
}

export function buildSsoOrganizationUserPickerDefaultColumnsState(
  activeCustomFieldDefinitions: SsoOrganizationPickerCustomFieldDefinition[],
  defaultColumnsState?: SsoOrganizationUserPickerColumnsState,
): SsoOrganizationUserPickerColumnsState {
  const defaults: SsoOrganizationUserPickerColumnsState = {
    id: { show: false },
    phone: { show: true },
    status: { show: true },
    is_online: { show: true },
    is_superuser: { show: false },
    expires_at: { show: false },
    platform_roles: { show: false },
    application_roles_summary: { show: false },
    organization_memberships_summary: { show: false },
    platform_role_assignments: { show: false },
    created_at: { show: false },
    updated_at: { show: false },
  };
  for (const definition of activeCustomFieldDefinitions) {
    defaults[`custom-${definition.field_key}`] = { show: false };
  }
  return {
    ...defaults,
    ...defaultColumnsState,
  };
}

export function useSsoOrganizationUserPickerColumns({
  customFieldDefinitions,
  filterValues,
  setFilterValues,
  defaultColumnsState,
}: UseSsoOrganizationUserPickerColumnsParams) {
  const applyDisplayNameFilter = useCallback(
    (displayName: string | undefined) => {
      setFilterValues((previous) => {
        if (displayName !== undefined) return { ...previous, display_name: displayName };
        const { display_name: _displayName, ...rest } = previous;
        return rest;
      });
    },
    [setFilterValues],
  );
  const applyUsernameFilter = useCallback(
    (username: string | undefined) => {
      setFilterValues((previous) => {
        if (username !== undefined) return { ...previous, username };
        const { username: _username, ...rest } = previous;
        return rest;
      });
    },
    [setFilterValues],
  );
  const displayNameFilter = useControlledTextHeaderFilter({
    value: filterValues.display_name,
    placeholder: t("common.identityPicker.searchField", {
      field: t("common.fields.displayName"),
    }),
    onApplyValue: applyDisplayNameFilter,
  });
  const usernameFilter = useControlledTextHeaderFilter({
    value: filterValues.username,
    placeholder: t("common.identityPicker.searchField", { field: t("common.fields.username") }),
    onApplyValue: applyUsernameFilter,
  });

  const activeCustomFieldDefinitions = useMemo(
    () => getActiveCustomFieldDefinitions(customFieldDefinitions),
    [customFieldDefinitions],
  );

  const mergedDefaultColumnsState = useMemo(
    () =>
      buildSsoOrganizationUserPickerDefaultColumnsState(
        activeCustomFieldDefinitions,
        defaultColumnsState,
      ),
    [activeCustomFieldDefinitions, defaultColumnsState],
  );

  const customFieldColumns = useMemo<ProColumns<SsoOrganizationPickerUser>[]>(
    () =>
      activeCustomFieldDefinitions.map((definition) => ({
        title: definition.label,
        key: `custom-${definition.field_key}`,
        dataIndex: ["custom_fields_json", definition.field_key],
        render: (_value: unknown, record) =>
          formatCustomFieldCellValue(record.custom_fields_json?.[definition.field_key]),
      })),
    [activeCustomFieldDefinitions],
  );

  const userColumns = useMemo<ProColumns<SsoOrganizationPickerUser>[]>(
    () => [
      {
        title: t("common.fields.userId"),
        dataIndex: "id",
      },
      {
        title: t("common.fields.displayName"),
        dataIndex: "display_name",
        filterDropdown: displayNameFilter.filterDropdown,
        filterIcon: displayNameFilter.filterIcon,
        ...(displayNameFilter.filteredValue === undefined ? {} : { filteredValue: displayNameFilter.filteredValue }),
        render: (_value, record) => (
          <IdentityUserText
            displayText={record.display_name}
            userId={record.id}
            username={record.username}
            fullName={record.display_name}
            detailLabels={{
              userId: t("common.fields.userId"),
              username: t("common.fields.username"),
              fullName: t("common.fields.displayName"),
            }}
          />
        ),
      },
      {
        title: t("common.fields.username"),
        dataIndex: "username",
        filterDropdown: usernameFilter.filterDropdown,
        filterIcon: usernameFilter.filterIcon,
        ...(usernameFilter.filteredValue === undefined ? {} : { filteredValue: usernameFilter.filteredValue }),
        render: (_value, record) => (
          <IdentityUserText
            userId={record.id}
            username={record.username}
            fullName={record.display_name}
            detailLabels={{
              userId: t("common.fields.userId"),
              username: t("common.fields.username"),
              fullName: t("common.fields.displayName"),
            }}
          />
        ),
      },
      {
        title: t("common.fields.organization"),
        dataIndex: "organization_name",
      },
      {
        title: t("common.fields.organizationUnit"),
        key: "organization_unit_names",
        render: (_value, record) => record.organization_unit_names.join(" / ") || "-",
      },
      {
        title: t("common.fields.phone"),
        dataIndex: "phone",
        render: (_value, record) => record.phone || "-",
      },
      {
        title: t("common.nouns.status"),
        dataIndex: "status",
        render: (_value, record) => {
          const statusDisplay = getIdentityStatusDisplay(record.status);
          return <Tag color={statusDisplay.tone}>{statusDisplay.label}</Tag>;
        },
      },
      {
        title: t("common.labels.online"),
        dataIndex: "is_online",
        render: (_value, record) => (
          <Tag color={record.is_online ? "success" : "default"}>
            {record.is_online ? t("common.labels.online") : t("common.labels.offline")}
          </Tag>
        ),
      },
      {
        title: t("common.security.superuser"),
        dataIndex: "is_superuser",
        render: (_value, record) => (
          <Tag color={record.is_superuser ? "gold" : "default"}>
            {record.is_superuser ? t("common.boolean.yes") : t("common.boolean.no")}
          </Tag>
        ),
      },
      {
        title: t("common.fields.expiresAt"),
        dataIndex: "expires_at",
        render: (_value, record) => formatAdminDateTime(record.expires_at),
      },
      {
        title: t("common.fields.platformRoles"),
        key: "platform_roles",
        render: (_value, record) => (
          <WrappableTagList labels={record.platform_roles} maxWidth="100%" />
        ),
      },
      {
        title: t("common.fields.applicationRolesSummary"),
        key: "application_roles_summary",
        render: (_value, record) => (
          <WrappableTagList labels={record.application_roles_summary} maxWidth="100%" />
        ),
      },
      {
        title: t("common.identityPicker.columns.allOrganizationMemberships"),
        key: "organization_memberships_summary",
        render: (_value, record) => formatMembershipSummary(record),
      },
      {
        title: t("common.fields.platformRoleAssignments"),
        key: "platform_role_assignments",
        render: (_value, record) => formatPlatformRoleAssignments(record),
      },
      {
        title: t("common.fields.createdAt"),
        dataIndex: "created_at",
        render: (_value, record) => formatAdminDateTime(record.created_at),
      },
      {
        title: t("common.fields.updatedAt"),
        dataIndex: "updated_at",
        render: (_value, record) => formatAdminDateTime(record.updated_at),
      },
      ...customFieldColumns,
    ],
    [customFieldColumns, displayNameFilter, usernameFilter],
  );

  return {
    mergedDefaultColumnsState,
    userColumns,
  };
}
